import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import {
  BdCompanyGroup,
  GroupMemberType,
} from '@bindoc-projects/resource-domain-lib';
import { Observable } from 'rxjs';
import { BdPermissionsGroupsStore } from '../store/group/groups.store';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';

/**
 * One entry of `GET /groups/{groupId}/members/`. `username` is the user's
 * login e-mail (UPN as fallback), not a display name — the endpoint returns no
 * display name or mail, so callers that show them resolve the user by id.
 */
export interface BdGroupMemberResponse {
  userId: string;
  username: string;
  type: GroupMemberType;
}

@Injectable({
  providedIn: 'root',
})
export class BdPermissionsGroupsAdminService {
  private _httpClient = inject(HttpClient);
  private _environmentService = inject(BdAppEnvironmentService);

  private _store: BdPermissionsGroupsStore;

  constructor() {
    this._store = new BdPermissionsGroupsStore(this._environmentService.load(ApiEndpointKey.BD_PERMISSION_RESOURCE_URL));
  }

  public getById(groupId: string): Observable<BdCompanyGroup | null> {
    const url = this._store.getById(groupId);
    return this._httpClient.get<BdCompanyGroup>(url);
  }

  /**
   * Renames a group (`PUT /groups/{id}/`). The name is the only field the
   * endpoint accepts, so nothing else is sent.
   */
  public update(groupId: string, name: string): Observable<BdCompanyGroup> {
    const url = this._store.getById(groupId);

    return this._httpClient.put<BdCompanyGroup>(url, { name });
  }

  public delete(groupId: string): Observable<void> {
    const url = this._store.getById(groupId);

    return this._httpClient.delete<void>(url);
  }

  public getMembers(groupId: string): Observable<BdGroupMemberResponse[]> {
    const url = this._store.members(groupId);

    return this._httpClient.get<BdGroupMemberResponse[]>(url);
  }

  /**
   * Adds a user to the group with the given permission type
   * @param groupId
   * @param userId
   * @param type
   */
  public addUserToGroup(
    groupId: string,
    userId: string,
    type: GroupMemberType,
  ): Observable<void> {
    const url = this._store.members(groupId);

    return this._httpClient.post<void>(url, {
      userId: userId,
      type: type,
    });
  }

  public removeUserFromGroup(
    groupId: string,
    userId: string,
  ): Observable<void> {
    const url = `${this._store.members(groupId)}${userId}/`;

    return this._httpClient.delete<void>(url);
  }

  public createGroup(
    companyId: string,
    groupName: string,
  ): Observable<BdCompanyGroup> {
    const url = this._store.index()
    const group: {
      companyId: string;
      name: string;
    } = {
      name: groupName,
      companyId: companyId,
    };

    return this._httpClient.post<BdCompanyGroup>(url, group);
  }
}
